/**
 * BenchmarksCategoryPanel (Story 37.5 — Phase 2 category panel #5)
 *
 * The `benchmarks` sidebar category's floating-panel body. Replaces the generic
 * `CardListPanel` (which dropped a blank `benchmark` widget) with a purpose-built
 * **configure-then-add** builder: the user picks an industry + metrics FIRST,
 * then Add attaches the populated config to a new `benchmark` widget so it
 * renders non-blank (AC #2, AC #6).
 *
 * Reuse, don't fork: the industry/metrics/toggle controls are the SHARED
 * `BenchmarkConfigSection` from `widgetDataForms` — the exact same sub-form the
 * `WidgetConfigPanel` uses when editing an existing benchmark (AC #4 anti-drift).
 * The sub-form's contract is `{ widget, onUpdate }`; this panel has no real
 * widget yet, so it wraps a local **draft config** in a synthetic draft-widget
 * and adapts `onUpdate` to merge patches into the draft (Pre-mortem A).
 *
 * Add is DISABLED until an industry is chosen AND ≥1 metric is selected
 * (`isBenchmarkDraftValid`) — the concrete "never blank" enforcement (AC #7).
 *
 * Single-type panel (NO type selector): `benchmark` is one widget type, so this
 * mirrors `ImagesCategoryPanel` (AC #8).
 */

import { useState, useMemo, useCallback } from "react";
import { Crosshair } from "lucide-react";
import type { WidgetConfig } from "../../lib/templateUtils";
import { DEFAULT_WIDGET_CONFIGS, DEFAULT_WIDGET_TITLES } from "./canvasWidgetDefaults";
import { BenchmarkConfigSection, isBenchmarkDraftValid } from "./widgetDataForms";
import type { CategoryPanelProps } from "./categoryPickerRegistry";

/** Synthetic draft-widget id — stable, never persisted (Pre-mortem A). */
const DRAFT_BENCHMARK_WIDGET_ID = "__draft_benchmark__";

export function BenchmarksCategoryPanel({ onWidgetAdd }: CategoryPanelProps) {
  // Seed the draft from the real default config (incl. `metricVisibility: {}`,
  // which the sub-form ignores but which the seed keeps so a configured-then-
  // added benchmark equals a blank drop — AC #2). structuredClone (not a shallow
  // spread) so the nested `metrics` array + `metricVisibility` object aren't
  // shared by reference with the module-level default (code review pass 1).
  const [draft, setDraft] = useState<Record<string, unknown>>(() =>
    structuredClone(DEFAULT_WIDGET_CONFIGS.benchmark),
  );

  const draftWidget = useMemo<WidgetConfig>(
    () => ({
      id: DRAFT_BENCHMARK_WIDGET_ID,
      type: "benchmark" as const,
      title: DEFAULT_WIDGET_TITLES.benchmark,
      position: { row: 0, col: 0, colSpan: 6, rowSpan: 3 },
      config: draft,
    }),
    [draft],
  );

  const handleDraftUpdate = useCallback(
    (_id: string, changes: Partial<WidgetConfig>) => {
      if (!changes.config) return;
      setDraft((d) => ({ ...d, ...(changes.config as Record<string, unknown>) }));
    },
    [],
  );

  const canAdd = isBenchmarkDraftValid(draft) && typeof onWidgetAdd === "function";

  const handleAdd = useCallback(() => {
    if (!canAdd || !onWidgetAdd) return;
    const added = onWidgetAdd("benchmark", structuredClone(draft));
    if (added) setDraft(structuredClone(DEFAULT_WIDGET_CONFIGS.benchmark));
  }, [canAdd, onWidgetAdd, draft]);

  return (
    <div className="flex h-full flex-col p-3" data-testid="benchmarks-category-panel">
      <div className="mb-3 flex items-center gap-2">
        <Crosshair size={18} className="text-muted-foreground" />
        <h3 className="text-[13px] font-semibold text-foreground">Benchmarks</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <BenchmarkConfigSection widget={draftWidget} onUpdate={handleDraftUpdate} />
      </div>

      <button
        type="button"
        data-testid="benchmarks-panel-add-btn"
        disabled={!canAdd}
        onClick={handleAdd}
        className="mt-3 w-full rounded-md border border-input bg-primary px-3 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Add to report
      </button>
    </div>
  );
}
